import { ADMIN_EMAIL, contactMailto } from '../lib/config'

const EFFECTIVE_DATE = 'May 31, 2026'

export default function PrivacyPage({ embedded = false }: { embedded?: boolean }) {
  const body = (
    <div className="space-y-10">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-stone-900">Privacy Policy</h1>
          <p className="text-stone-500 text-sm">Proof of Work — Your record of professional impact</p>
          <p className="text-stone-400 text-xs pt-1">Effective {EFFECTIVE_DATE}</p>
        </div>

        <Section title="Introduction">
          <p>
            Proof of Work (<strong>getproofofwork.com</strong>) is a professional CV builder
            operated by its individual developer, based in Israel (&ldquo;we&rdquo;, &ldquo;us&rdquo;,
            &ldquo;the Operator&rdquo;). This Privacy Policy explains what data we collect when
            you use the Service, how we use it, and your rights in relation to it.
          </p>
          <p className="mt-2">
            Questions about this policy: <a href={contactMailto()} className="text-stone-700 underline underline-offset-2">{ADMIN_EMAIL}</a>
          </p>
        </Section>

        <Section title="Data we collect">
          <p className="font-medium text-stone-700 mb-1">Information you provide</p>
          <ul className="list-disc pl-4 space-y-1">
            <li>Name and email address from your Google account</li>
            <li>Professional accomplishments, CV documents, and work history you upload or enter</li>
            <li>Contact details and profile information you add to your account</li>
            <li>An API key for a third-party AI provider, if you choose to supply one</li>
          </ul>

          <p className="font-medium text-stone-700 mb-1 mt-4">Data generated from your content</p>
          <ul className="list-disc pl-4 space-y-1">
            <li>Structured accomplishment records extracted from your uploaded materials</li>
            <li>Capability tags and Perspective labels associated with your accomplishments</li>
            <li>Generated CV documents and their revision history</li>
          </ul>

          <p className="font-medium text-stone-700 mb-1 mt-4">Usage and account data</p>
          <ul className="list-disc pl-4 space-y-1">
            <li>Account creation date and last active timestamp</li>
            <li>Number of CVs generated and editing iterations per CV</li>
            <li>Number of AI questions asked during each CV generation session</li>
            <li>Estimated AI processing cost associated with your account (managed-key users only)</li>
          </ul>

          <p className="font-medium text-stone-700 mb-1 mt-4">Technical data</p>
          <ul className="list-disc pl-4 space-y-1">
            <li>Session identifiers stored in httpOnly cookies (see the Cookies section)</li>
            <li>Server access logs including IP address, retained for security monitoring</li>
            <li>
              Aggregate page-view and performance measurements collected by Vercel Web
              Analytics and Speed Insights — the page visited, referring site, approximate
              country/region, device type, browser and operating system, and page load timings.
              These are anonymous and aggregated: no cookie is set, no identifier follows you
              between sites, and the measurements are not linked to your account. We strip
              invite tokens and CV session identifiers from the recorded address before it is
              sent.
            </li>
          </ul>
        </Section>

        <Section title="How we use your data">
          <p>We use your data solely to:</p>
          <ol className="list-decimal pl-4 space-y-2 mt-2">
            <li>
              <strong>Operate and deliver the Service to you</strong> — including storing your
              accomplishments, generating CVs, and displaying your Library.
              Lawful basis: performance of contract.
            </li>
            <li>
              <strong>Send transactional email</strong> — such as account notifications and
              data export links. Lawful basis: performance of contract.
            </li>
            <li>
              <strong>Record anonymised interaction metrics</strong> to evaluate whether the
              tool is working well and how it is used. These metrics do not include your CV
              content or accomplishment text. Lawful basis: legitimate interests.
            </li>
            <li>
              <strong>Comply with legal obligations</strong> — including responding to valid
              court orders or legal process.
            </li>
          </ol>
          <p className="mt-3">
            We do not sell your data, share it with advertisers, or use it to train AI models.
          </p>
        </Section>

        <Section title="AI processing">
          <p className="font-medium text-stone-700 mb-1">Using our managed API key (default)</p>
          <p>
            When you use Proof of Work with our managed API key, your accomplishment text and
            CV content are sent to our current AI provider for processing. Our current provider
            is{' '}
            <a href="https://www.anthropic.com/privacy" target="_blank" rel="noreferrer"
               className="text-stone-700 underline underline-offset-2">Anthropic, Inc.</a>{' '}
            We reserve the right to change AI providers as needed to operate the Service and
            will update this policy when we do. By default, Anthropic&apos;s API does not use
            inputs to train their models.
          </p>

          <p className="font-medium text-stone-700 mb-1 mt-4">Using your own API key (BYO)</p>
          <p>
            If you supply your own API key in Settings, your content is sent to the provider
            you configured — not to ours. We are not responsible for the data practices of
            providers you choose. Review that provider&apos;s privacy policy before use.
          </p>
        </Section>

        <Section title="Third-party sub-processors">
          <p className="mb-3">The following services process data as part of operating Proof of Work:</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="text-left py-2 pr-4 font-medium text-stone-700 w-1/4">Service</th>
                  <th className="text-left py-2 pr-4 font-medium text-stone-700 w-1/2">Purpose</th>
                  <th className="text-left py-2 font-medium text-stone-700">Privacy policy</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                <SubprocessorRow
                  name="Supabase"
                  purpose="Database, authentication, and file storage"
                  url="https://supabase.com/privacy"
                />
                <SubprocessorRow
                  name="Anthropic"
                  purpose="AI processing (managed-key users only)"
                  url="https://www.anthropic.com/privacy"
                />
                <SubprocessorRow
                  name="Resend"
                  purpose="Transactional email"
                  url="https://resend.com/privacy"
                />
                <SubprocessorRow
                  name="Vercel"
                  purpose="Frontend hosting, and anonymous page-view and performance analytics"
                  url="https://vercel.com/legal/privacy-policy"
                />
                <SubprocessorRow
                  name="Render"
                  purpose="Backend hosting"
                  url="https://render.com/privacy"
                />
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="Who can access your content">
          <p>
            Under normal operations, the Operator cannot read your CV content or professional
            accomplishments. The Operator has access only to:
          </p>
          <ul className="list-disc pl-4 space-y-1 mt-2">
            <li>Your email address and account status</li>
            <li>Usage counts and cost estimates associated with your account</li>
            <li>Non-identifying interaction metrics (CV counts, iteration counts, question counts)</li>
          </ul>
          <p className="mt-3">The Operator may access your content in two exceptional circumstances:</p>
          <ol className="list-decimal pl-4 space-y-2 mt-2">
            <li>
              <strong>Legal process:</strong> If required by a valid court order or other
              legally binding process issued by a court of competent jurisdiction, we will
              comply with the obligation to disclose.
            </li>
            <li>
              <strong>Debugging with your explicit consent:</strong> If you report a technical
              issue and explicitly request hands-on assistance, we may access the relevant data
              with your written consent to diagnose and resolve the problem. We will not access
              content without that consent.
            </li>
          </ol>
        </Section>

        <Section title="Data retention">
          <p>
            <strong>Active accounts:</strong> Data is retained for as long as your account
            exists.
          </p>
          <p className="mt-2">
            <strong>After account deletion:</strong> All personal data — your name, email,
            CV content, accomplishments, generated CVs, profile details, and linked usage
            records — will be permanently erased within 30 days of account deletion.
          </p>
          <p className="mt-2">
            <strong>What we may retain after deletion:</strong>
          </p>
          <ul className="list-disc pl-4 space-y-2 mt-1">
            <li>
              <em>Anonymised aggregate record:</em> A summary row containing no personal
              identifiers — only aggregate counts such as total CVs generated and total
              spend — may be retained for product analytics and cost accounting. This record
              cannot be linked back to you once your identifying information is removed.
            </li>
            <li>
              <em>System vocabulary:</em> Capability terms that entered the service&apos;s
              controlled vocabulary as a result of any user&apos;s content are categorical
              semantic terms, not personal data, and are retained as part of the service
              infrastructure.
            </li>
            <li>
              <em>Legal and financial records:</em> Where required by Israeli accounting
              regulations or other applicable law, minimal transaction records may be retained
              for up to seven years.
            </li>
          </ul>
          <p className="mt-2">
            <strong>Backup systems:</strong> Personal data in backup copies is overwritten
            within the same 30-day window.
          </p>
        </Section>

        <Section title="Your rights">
          <p className="font-medium text-stone-700 mb-1">All users</p>
          <ul className="list-disc pl-4 space-y-1">
            <li><strong>Access:</strong> Request a copy of data we hold about you (available via Settings → Data → Download My Data)</li>
            <li><strong>Rectification:</strong> Request correction of inaccurate personal data</li>
            <li><strong>Erasure:</strong> Request deletion of your data (available via Settings → Data → Delete My Account)</li>
            <li><strong>Portability:</strong> Receive your data in a machine-readable format (available via Download My Data)</li>
          </ul>

          <p className="font-medium text-stone-700 mb-1 mt-4">EU and EEA residents (GDPR)</p>
          <p className="mb-1">In addition to the above, you have the right to:</p>
          <ul className="list-disc pl-4 space-y-1">
            <li><strong>Restrict processing</strong> in certain circumstances</li>
            <li><strong>Object</strong> to processing based on legitimate interests</li>
            <li><strong>Withdraw consent</strong> at any time where processing is based on consent, without affecting the lawfulness of prior processing</li>
            <li><strong>Lodge a complaint</strong> with a data protection supervisory authority in your EU member state or country of residence</li>
          </ul>

          <p className="font-medium text-stone-700 mb-1 mt-4">Israeli residents</p>
          <p>
            You have rights under the Privacy Protection Law 5741-1981 and its regulations,
            including the right to inspect, correct, and request deletion of personal
            information held about you.
          </p>

          <p className="mt-3">
            To exercise any of these rights, contact{' '}
            <a href={contactMailto()} className="text-stone-700 underline underline-offset-2">{ADMIN_EMAIL}</a>.
            We will respond within 30 days.
          </p>
        </Section>

        <Section title="Cookies and sessions">
          <p>
            Proof of Work uses a single session cookie to keep you logged in. This cookie:
          </p>
          <ul className="list-disc pl-4 space-y-1 mt-2">
            <li>Is an httpOnly cookie — not accessible to JavaScript running on the page</li>
            <li>Expires after 7 days of inactivity and refreshes automatically on use</li>
            <li>Is invalidated immediately when you sign out</li>
          </ul>
          <p className="mt-2">
            We do not use advertising cookies, tracking pixels, or third-party analytics
            cookies. We do use Vercel Web Analytics and Speed Insights, which measure page
            views and page performance without cookies: visitors are counted using a
            short-lived anonymous hash that is discarded within 24 hours and cannot be used to
            follow you across sites or link activity to your account. See{' '}
            <span className="font-medium">Data we collect → Technical data</span> for exactly
            what is measured.
          </p>
        </Section>

        <Section title="International data transfers">
          <p>
            Our infrastructure providers (Supabase, Anthropic, Vercel, Render) may store or
            process data in the United States or other countries outside the European Economic
            Area (EEA). Where your data is transferred outside the EEA, we rely on appropriate
            safeguards, including Standard Contractual Clauses (SCCs) with our sub-processors.
          </p>
          <p className="mt-2">
            Israel holds an EU adequacy decision, meaning transfers of personal data from the
            EEA to Israel are treated as equivalent to intra-EU transfers.
          </p>
        </Section>

        <Section title="Age restriction">
          <p>
            The Service is intended for users who are at least 18 years old. By creating an
            account, you confirm that you meet this requirement. We do not knowingly collect
            data from persons under 18. If we become aware that a user is under 18, we will
            close the account and delete the associated data promptly.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            If we make material changes to this Privacy Policy, we will notify you by email
            at least 14 days before the changes take effect. Continued use of the Service
            after that date constitutes acceptance of the revised policy. The effective date
            at the top of this page will always reflect the current version.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            For privacy enquiries, data subject requests, or concerns, contact us at{' '}
            <a href={contactMailto()} className="text-stone-700 underline underline-offset-2">{ADMIN_EMAIL}</a>.
          </p>
        </Section>
    </div>
  )

  // Embedded mode (e.g. the onboarding modal) renders just the policy body; the
  // host supplies the scroll container and dismiss affordance.
  if (embedded) return body

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-12">{body}</main>
      <footer className="py-4 text-center text-xs text-stone-400 border-t border-stone-100">
        <div className="flex items-center justify-center gap-4">
          <a href="/" className="hover:text-stone-600 transition-colors">Back to Proof of Work</a>
          <span>·</span>
          <a href="/terms" className="hover:text-stone-600 transition-colors">Terms of Service</a>
        </div>
      </footer>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-base font-medium text-stone-800">{title}</h2>
      <div className="text-stone-600 text-sm leading-relaxed">{children}</div>
    </section>
  )
}

function SubprocessorRow({
  name,
  purpose,
  url,
}: {
  name: string
  purpose: string
  url: string
}) {
  return (
    <tr>
      <td className="py-2 pr-4 text-stone-700">{name}</td>
      <td className="py-2 pr-4 text-stone-600">{purpose}</td>
      <td className="py-2">
        <a href={url} target="_blank" rel="noreferrer"
           className="text-stone-700 underline underline-offset-2 hover:text-stone-900">
          Policy
        </a>
      </td>
    </tr>
  )
}
